<template>
    <Transition name="zone-tooltip">
        <div
            v-if="zone"
            class="zone-map-tooltip"
            :style="tooltipStyle"
            role="tooltip"
            :aria-label="zone.name ?? zone.nombre"
        >
            <!-- Nombre de la zona -->
            <p class="tw:font-bold tw:text-sm tw:m-0 tw:mb-1 tw:text-gray-900 tw:leading-tight">
                {{ zone.name ?? zone.nombre }}
            </p>

            <template v-if="isAvailable">
                <!-- Disponibilidad -->
                <p class="tw:text-xs tw:text-teal-700 tw:font-semibold tw:m-0 tw:mb-1">
                    {{ $t('mapa.disponible') }}
                </p>

                <!-- Libres -->
                <p v-if="libres != null" class="tw:text-xs tw:text-gray-700 tw:m-0 tw:mb-1">
                    {{ $t('general.libres') }}:
                    <span class="tw:font-semibold">{{ libres }}</span>
                </p>

                <!-- Precios disponibles -->
                <template v-if="precios.length > 0">
                    <p class="tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:m-0 tw:mt-2 tw:mb-1">
                        {{ $t('mapa.precios_disponibles') }}
                    </p>
                    <ul class="tw:list-none tw:m-0 tw:p-0 tw:flex tw:flex-col tw:gap-0.5">
                        <li
                            v-for="precio in precios"
                            :key="precio.id ?? precio.descripcion"
                            class="tw:text-xs tw:text-gray-700"
                        >
                            {{ precio.descripcion }} —
                            <span class="tw:font-semibold">{{ formatearPrecio(precio.precio) }}</span>
                        </li>
                    </ul>
                </template>
            </template>

            <template v-else>
                <p class="tw:text-xs tw:text-red-600 tw:font-semibold tw:m-0">
                    {{ $t('mapa.no_disponible') }}
                </p>
            </template>
        </div>
    </Transition>
</template>

<script setup>
    import { computed } from 'vue';
    import { useFilters } from '@/composables/front/formatters/useFilters';

    const props = defineProps({
        /**
         * El objeto zona completo tal como llega del backend/servicio.
         * Cuando es `null`, el tooltip no se muestra.
         */
        zone: {
            type: Object,
            default: null,
        },
        /**
         * Coordenada X (px) relativa al contenedor `position: relative` donde se
         * renderiza el tooltip. Viene del clientX del mouseenter sobre la zona SVG.
         */
        x: {
            type: Number,
            default: 0,
        },
        /**
         * Coordenada Y (px) relativa al contenedor `position: relative` donde se
         * renderiza el tooltip. Viene del clientY del mouseenter sobre la zona SVG.
         */
        y: {
            type: Number,
            default: 0,
        },
    });

    const { formatearPrecio } = useFilters();

    /**
     * Posición dinámica del tooltip.
     * Se desplaza 12px a la izquierda y 12px hacia arriba respecto al cursor,
     * de forma que quede visualmente "encima" de la zona hovereada.
     */
    const tooltipStyle = computed(() => ({
        left: `${props.x + 12}px`,
        top: `${props.y - 12}px`,
        transform: 'translateY(-100%)',
    }));

    const isAvailable = computed(() =>
        props.zone
            ? (props.zone.available ?? Boolean(props.zone.esta_disponible))
            : false
    );

    /** Plazas libres: usa `availability` (campo normalizado) o `libres` (legacy) */
    const libres = computed(() => {
        if (!props.zone) return null;
        const val = props.zone.availability ?? props.zone.libres ?? null;
        if (val == null) return null;
        return Math.max(0, val);
    });

    /** Lista de precios tal como viene del backend */
    const precios = computed(() => {
        if (!props.zone || !Array.isArray(props.zone.precios)) return [];
        return props.zone.precios;
    });
</script>

<style scoped>
.zone-map-tooltip {
    position: absolute;
    z-index: 20;
    min-width: 160px;
    max-width: 220px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
    padding: 10px 14px;
    pointer-events: none;
}

/* Transition */
.zone-tooltip-enter-active,
.zone-tooltip-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.zone-tooltip-enter-from,
.zone-tooltip-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
